import {
  DateField,
  ExportButton,
  List,
  RefreshButton,
  SaveButton,
  ShowButton,
  TagField,
  TextField,
  useTable,
} from "@refinedev/antd";

import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  FormProps,
  Input,
  Progress,
  Row,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import {
  CrudFilters,
  IResourceComponentsProps,
  getDefaultFilter,
  useCustomMutation,
  useExport,
  useNotification,
  useApiUrl,
} from "@refinedev/core";

import { IBulkEmailJob } from "interfaces";
import React from "react";
import { MailOutlined } from "@ant-design/icons";
import { getStatusTagColor } from "constants/tagColors";

export const BulkEmailJobsList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, tableQuery: { isLoading, refetch }, searchFormProps, filters } = useTable<IBulkEmailJob>({
    resource: "bulk-email",

    onSearch: (params: any) => {
      const filters: CrudFilters = [];
      const { subject, status, vesselIds } = params;

      filters.push({
        field: "subject",
        operator: "contains",
        value: subject ? subject : undefined,
      });
      filters.push({
        field: "status",
        operator: "eq",
        value: status ? status : undefined,
      });
      filters.push({
        field: "vesselIds",
        operator: "contains",
        value: vesselIds ? vesselIds : undefined,
      });

      return filters;
    },

    pagination: {
      pageSize: 20,
      mode: "server"
    },

    filters: {
      defaultBehavior: "replace"
    }
  });
  const { open } = useNotification();
  const apiUrl = useApiUrl();

  const handleRefresh = async () => {
    const result = await refetch();    
  };

  const { triggerExport, isLoading: isLoadingExport } = useExport<IBulkEmailJob>({
    mapData: (item) => {
      return {
        "ID": item?.id,
        "Name": item?.name || "",
        "Description": item?.description || "",
        "Status": item?.status || "",
        "Vessel IDs": item?.vesselIds ? item.vesselIds.join(", ") : "",
        "Total Vessels": item?.totalVessels || 0,
        "Processed Vessels": item?.processedVessels || 0,
        "Successful Emails": item?.successfulEmails || 0,
        "Failed Emails": item?.failedEmails || 0,
        "Created By": item?.createdBy || "",
        "Started At": item?.startedAt ? new Date(item.startedAt).toISOString() : "",
        "Completed At": item?.completedAt ? new Date(item.completedAt).toISOString() : "",
      };
    },
    unparseConfig: {
      header: true,
      columns: [
        "ID",
        "Name",
        "Description",
        "Status",
        "Vessel IDs",
        "Total Vessels",
        "Processed Vessels",
        "Successful Emails",
        "Failed Emails",
        "Created By",
        "Started At",
        "Completed At",
      ],
    },
  });

  const { mutate: triggerScheduler, mutation: {
    isPending: isSchedulerLoading
  } } = useCustomMutation();

  const handleSchedulerTrigger = () => {
    triggerScheduler(
      {
        url: `${apiUrl}/scheduler/bulk-email-files/check`,
        method: "post",
        values: {},
      },
      {
        onSuccess: () => {
          open?.({
            type: "success",
            message: "Scheduler triggered successfully",
            description: "Pending eNTM email files have been scheduled",
          });
        },
        onError: (error) => {
          open?.({
            type: "error",
            message: "Failed to trigger scheduler",
            description: error?.message || "An error occurred",
          });
        },
      }
    );
  };

  const calculateProgress = (record: IBulkEmailJob) => {
    if (record.totalVessels === 0) return 0;
    return Math.round((record.processedVessels / record.totalVessels) * 100);
  };

  const columns: ColumnsType<IBulkEmailJob> = [
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      width: 250,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (value) => (
        <TagField color={getStatusTagColor(value)} value={value} />
      ),
    },
    {
      title: "Vessel IDs",
      dataIndex: "vesselIds",
      key: "vesselIds",
      width: 200,
      className: "wrap-text",
      render: (value: string[] | null) => (
        <TextField value={value ? value.join(", ") : "N/A"} />
      ),
    },
    {
      title: "Progress",
      key: "progress",
      width: 150,
      render: (_, record) => (
        <Progress
          percent={calculateProgress(record)}
          size="small"
          strokeColor="green"
          format={() => `${record.processedVessels}/${record.totalVessels}`}
        />
      ),
    },
    {
      title: "Completed At",
      dataIndex: "completedAt",
      key: "completedAt",
      width: 180,
      render: (value) => value ? <DateField value={value} format="DD-MMM-YYYY HH:mm" /> : <TextField value="N/A" />,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 80,
      render: (_, record) => (
        <Space>
          <ShowButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        // onBack: () => window.history.back(),
        title: "eNTM Emails",
        subTitle: "bulk email jobs and eNTM vessel email status",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <React.Fragment>
            <Space>
              <Button
                type="primary"
                icon={<MailOutlined />}
                loading={isSchedulerLoading}
                onClick={handleSchedulerTrigger}
              >
                Trigger Emails
              </Button>
              <RefreshButton onClick={handleRefresh} />
              <ExportButton onClick={triggerExport} loading={isLoadingExport} />
            </Space>
          </React.Fragment>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Row gutter={[16, 16]}>
        <Col xl={24} lg={24} xs={24}>
          <Card type="inner" title={"Filter"}>
            <Filter formProps={searchFormProps} filters={filters || []} />
          </Card>
        </Col>
        <Col xl={24} xs={24}>
          <Table
            {...tableProps}
            columns={columns}
            rowKey="id"
            size="middle"
            scroll={{ x: 1200 }}
            sticky
            pagination={{
              ...tableProps.pagination,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
            }}
          />
        </Col>
      </Row>
    </List>
  );
};

const Filter: React.FC<{ formProps: FormProps; filters: CrudFilters }> = (
  props
) => {
  const { formProps, filters } = props;

  return (
    <Form
      layout="vertical"
      {...formProps}
      initialValues={{
        subject: getDefaultFilter("subject", filters),
        status: getDefaultFilter("status", filters),
        vesselIds: getDefaultFilter("vesselIds", filters),
      }}
    >
      <Row gutter={[16, 16]} align="middle" justify="space-between">
        <Col xs={24} sm={24} md={7}>
          <Form.Item label={"Subject"} name="subject">
            <Input placeholder="filter by subject" allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={7}>
          <Form.Item label={"Status"} name="status">
            <Select placeholder="filter by status" allowClear>
              <Select.Option value="pending">pending</Select.Option>
              <Select.Option value="processing">processing</Select.Option>
              <Select.Option value="completed">completed</Select.Option>
              <Select.Option value="failed">failed</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={7}>
          <Form.Item label={"Vessel IDs"} name="vesselIds">
            <Input placeholder="search vessel IDs (full or partial)" allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={3}>
          <Form.Item style={{ marginBottom: 0 }}>
            <SaveButton onClick={formProps.form?.submit} size="large" block>
              Filter
            </SaveButton>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};